import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Check, X, Loader2, BookOpen } from 'lucide-react';

const PendingApprovals = ({ onUpdate }) => {
  const [proofreaders, setProofreaders] = useState([]);
  const [translators, setTranslators] = useState([]);
  const [writers, setWriters] = useState([]);
  const [bookRequests, setBookRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const [proofreaderRes, writerRes, translatorRes, bookRequestRes] = await Promise.all([
        axios.get(`${API}/admin/pending-proofreaders`),
        axios.get(`${API}/admin/pending-writers`),
        axios.get(`${API}/admin/pending-translators`),
        axios.get(`${API}/admin/book-requests?status=pending`)
      ]);
      setProofreaders(proofreaderRes.data);
      setWriters(writerRes.data);
      setTranslators(translatorRes.data);
      setBookRequests(bookRequestRes.data);
    } catch (error) {
      toast.error('Kon nie aansoeke laai nie');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, role) => {
    setProcessing(userId);
    try {
      let endpoint;
      if (role === 'writer') {
        endpoint = `${API}/admin/approve-writer/${userId}`;
      } else if (role === 'proofreader') {
        endpoint = `${API}/admin/approve-proofreader/${userId}`;
      } else if (role === 'translator') {
        endpoint = `${API}/admin/approve-translator/${userId}`;
      }
      await axios.post(endpoint);
      const roleNames = { writer: 'Skrywer', proofreader: 'Proefleser', translator: 'Vertaler' };
      toast.success(`${roleNames[role]} goedgekeur!`);
      fetchPendingUsers();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Kon nie goedkeur nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId, role) => {
    setProcessing(userId);
    try {
      let endpoint;
      if (role === 'writer') {
        endpoint = `${API}/admin/reject-writer/${userId}`;
      } else if (role === 'proofreader') {
        endpoint = `${API}/admin/reject-proofreader/${userId}`;
      } else if (role === 'translator') {
        endpoint = `${API}/admin/reject-translator/${userId}`;
      }
      await axios.post(endpoint);
      toast.success('Aansoek afgekeur');
      fetchPendingUsers();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Kon nie afkeur nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveBookRequest = async (requestId) => {
    setProcessing(requestId);
    try {
      await axios.post(`${API}/admin/book-request/${requestId}/approve`);
      toast.success('Boek aanvraag goedgekeur!');
      fetchPendingUsers();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon nie goedkeur nie');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectBookRequest = async () => {
    if (!rejectTarget) return;
    setProcessing(rejectTarget);
    try {
      const formData = new FormData();
      formData.append('reason', rejectReason);
      await axios.post(`${API}/admin/book-request/${rejectTarget}/reject`, formData);
      toast.success('Boek aanvraag afgekeur');
      setShowRejectDialog(false);
      setRejectTarget(null);
      setRejectReason('');
      fetchPendingUsers();
      onUpdate && onUpdate();
    } catch (error) {
      toast.error('Kon nie afkeur nie');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Nuwe Aansoeke</h2>
        <p className="text-gray-600">Keur of weier skrywer, proefleser, vertaler aansoeke en boek aanvrae</p>
      </div>

      {/* Book Requests */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-green-600" />
          Boek Aanvrae ({bookRequests.length})
        </h3>
        {bookRequests.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><p className="text-gray-500">Geen hangende boek aanvrae nie</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {bookRequests.map((req) => (
              <Card key={req.id} className="border-green-100">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900">{req.book_title}</h3>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div><span className="text-gray-500">Skrywer:</span> {req.author_name}</div>
                        <div><span className="text-gray-500">Bladsye:</span> {req.page_count}</div>
                        <div><span className="text-gray-500">Vertaler:</span> {req.translator_name}</div>
                        <div><span className="text-gray-500">Datum:</span> {new Date(req.request_date).toLocaleDateString('af-ZA')}</div>
                      </div>
                      <div className="pt-2 text-sm">
                        <span className="font-medium text-green-600">
                          Betaling: R{req.page_count >= 100 ? '300' : '150'} (basis)
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleApproveBookRequest(req.id)}
                        disabled={processing === req.id}
                        data-testid="approve-book-request-btn"
                      >
                        {processing === req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => { setRejectTarget(req.id); setShowRejectDialog(true); }}
                        disabled={processing === req.id}
                        data-testid="reject-book-request-btn"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Translator Approvals */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Vertaler Aansoeke ({translators.length})</h3>
        {translators.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><p className="text-gray-500">Geen hangende vertaler aansoeke nie</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {translators.map((tr) => (
              <Card key={tr.id} className="border-green-100">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900">{tr.voornaam} {tr.van}</h3>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div><span className="text-gray-500">E-pos:</span> {tr.email}</div>
                        <div><span className="text-gray-500">Geboortedatum:</span> {tr.geboortedatum}</div>
                        <div><span className="text-gray-500">Toets Telling:</span> <span className="font-semibold text-green-600">{tr.test_score}%</span></div>
                        <div><span className="text-gray-500">Registrasie:</span> {new Date(tr.registration_date).toLocaleDateString('af-ZA')}</div>
                      </div>
                      <div className="pt-2">
                        <p className="text-sm font-semibold text-gray-700">Bank Besonderhede:</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600">
                          <div>Bank: {tr.bank_naam}</div>
                          <div>Rekening: {tr.rekeningnommer}</div>
                          <div>Tipe: {tr.rekeningtipe}</div>
                          <div>Tak: {tr.takkode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleApprove(tr.id, 'translator')}
                        disabled={processing === tr.id}
                        data-testid="approve-translator-btn"
                      >
                        {processing === tr.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(tr.id, 'translator')}
                        disabled={processing === tr.id}
                        data-testid="reject-translator-btn"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Writer Approvals */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Skrywer Aansoeke ({writers.length})</h3>
        {writers.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><p className="text-gray-500">Geen hangende skrywer aansoeke nie</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {writers.map((writer) => (
              <Card key={writer.id}>
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <h3 className="text-xl font-bold text-gray-900">{writer.voornaam} {writer.van}</h3>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        <div><span className="text-gray-500">E-pos:</span> {writer.email}</div>
                        <div><span className="text-gray-500">Geboortedatum:</span> {writer.geboortedatum}</div>
                        <div><span className="text-gray-500">Registrasie:</span> {new Date(writer.registration_date).toLocaleDateString('af-ZA')}</div>
                      </div>
                      <div className="pt-2">
                        <p className="text-sm font-semibold text-gray-700">Bank Besonderhede:</p>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600">
                          <div>Bank: {writer.bank_naam}</div>
                          <div>Rekening: {writer.rekeningnommer}</div>
                          <div>Tipe: {writer.rekeningtipe}</div>
                          <div>Tak: {writer.takkode}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="bg-green-500 hover:bg-green-600"
                        onClick={() => handleApprove(writer.id, 'writer')}
                        disabled={processing === writer.id}
                      >
                        {processing === writer.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(writer.id, 'writer')}
                        disabled={processing === writer.id}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Proofreader Approvals */}
      <div>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Proefleser Aansoeke ({proofreaders.length})</h3>
        {proofreaders.length === 0 ? (
          <Card><CardContent className="py-8 text-center"><p className="text-gray-500">Geen hangende proefleser aansoeke nie</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {proofreaders.map((pr) => (
            <Card key={pr.id}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <h3 className="text-xl font-bold text-gray-900">{pr.voornaam} {pr.van}</h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div><span className="text-gray-500">E-pos:</span> {pr.email}</div>
                      <div><span className="text-gray-500">Geboortedatum:</span> {pr.geboortedatum}</div>
                      <div><span className="text-gray-500">Toets Telling:</span> <span className="font-semibold text-green-600">{pr.test_score}%</span></div>
                      <div><span className="text-gray-500">Registrasie:</span> {new Date(pr.registration_date).toLocaleDateString('af-ZA')}</div>
                    </div>
                    <div className="pt-2">
                      <p className="text-sm font-semibold text-gray-700">Bank Besonderhede:</p>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm text-gray-600">
                        <div>Bank: {pr.bank_naam}</div>
                        <div>Rekening: {pr.rekeningnommer}</div>
                        <div>Tipe: {pr.rekeningtipe}</div>
                        <div>Tak: {pr.takkode}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      data-testid="approve-proofreader-btn"
                      size="sm"
                      className="bg-green-500 hover:bg-green-600"
                      onClick={() => handleApprove(pr.id, 'proofreader')}
                      disabled={processing === pr.id}
                    >
                      {processing === pr.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                    <Button
                      data-testid="reject-proofreader-btn"
                      size="sm"
                      variant="destructive"
                      onClick={() => handleReject(pr.id, 'proofreader')}
                      disabled={processing === pr.id}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}
      </div>

      {/* Reject Book Request Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Keur Boek Aanvraag Af</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Rede vir afkeuring (opsioneel)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Kanselleer</Button>
            <Button variant="destructive" onClick={handleRejectBookRequest}>Keur Af</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingApprovals;